import { useState } from 'react'
import { Database, KeyRound, ShieldCheck, Sparkles, ChevronRight } from 'lucide-react'

import ConnectionDefineFlow, { connectionAnswerText } from '@/components/connections/ConnectionDefineFlow'
import IOFlowConnectForm from '@/components/agent/IOFlowConnectForm'
import IOFlowPublishForm from '@/components/agent/IOFlowPublishForm'

/** Option text that means "the user will define the connection" — those clicks
 *  open the connection form instead of sending the label as the answer. */
const PROVIDE_CONNECTION_RE = /\b(provide|give|enter|define|set ?up)\b.*\bconnection\b|\bconnection details\b/i

/** Options the IOFlow forms replace, so the same choice is not offered twice
 *  — once as a button that collects the secret safely, once as plain text the
 *  user might answer by typing the secret into the chat. */
const CONNECT_APP_RE = /\bconnect\b|\bauthoriz|\bauthoris|\bcredential/i
const PUBLISH_RE = /\bauthoriz(?:ation)? code\b|\brequest approval\b|\bapprov/i

/** Which secret-collecting form this question needs, if any. */
export type AskFormKind = 'connection' | 'ioflow_connect' | 'ioflow_publish'

interface AgentAskBubbleProps {
  question: string
  options: string[]
  /** ``connectionId`` is set when the answer came from the connection form —
   *  it is the exact registered connection the user picked, so the backend
   *  binds to it instead of fishing the id out of the answer prose. */
  onAnswer: (answer: string, connectionId?: string) => void
  /** When true the bubble is locked (the question was already answered). */
  answered?: boolean
  /** The answer the user gave, shown when `answered`. */
  answeredWith?: string | null
  /** The agent flagged this as a database-connection problem: offer the
   *  connection-definition form (needs `project` to save into). */
  connectionForm?: boolean
  /** A form whose answer is a SECRET, so it is collected by the platform and
   *  the agent is told only an id. Set from the ask's `kind`. */
  formKind?: AskFormKind
  /** IOFlow application the connect form should pre-select. */
  appId?: string
  /** Seed application the publish form would publish. */
  seedJson?: unknown
  /** Project the connection form saves into (Metadata_Model/connections). */
  project?: string
}

/**
 * The Ask-AI clarifying-question bubble: the agent's question plus 2-4
 * suggested, clickable answers AND an always-available free-text field so the
 * user can either select or type — the Claude-Code-style "ask, don't guess"
 * prompt. Shared by all three Ask-AI backends (LLM planner, Claude Code,
 * Codex) so the experience is identical everywhere.
 *
 * When the agent flags the question as a connection problem
 * (`connectionForm`), the "I will provide the connection" choice opens the
 * platform's connection editor; the saved connection is persisted in the
 * project and its id is sent back as the answer so the agent continues on it.
 */
export default function AgentAskBubble({
  question,
  options,
  onAnswer,
  answered = false,
  answeredWith = null,
  connectionForm = false,
  formKind,
  appId,
  seedJson,
  project,
}: AgentAskBubbleProps) {
  const [value, setValue] = useState('')
  const [connOpen, setConnOpen] = useState(false)
  const [ioflowConnectOpen, setIoflowConnectOpen] = useState(false)
  const [ioflowPublishOpen, setIoflowPublishOpen] = useState(false)

  const formAvailable = (connectionForm || formKind === 'connection') && !!project
  const ioflowConnect = formKind === 'ioflow_connect' && !!project
  const ioflowPublish = formKind === 'ioflow_publish' && !!project

  // Options a form replaces are dropped from the plain list: offering the same
  // choice twice invites the user to answer by typing the secret into the chat,
  // which is exactly what the form exists to prevent.
  let plainOptions = options
  if (formAvailable) plainOptions = plainOptions.filter((o) => !PROVIDE_CONNECTION_RE.test(o))
  if (ioflowConnect) plainOptions = plainOptions.filter((o) => !CONNECT_APP_RE.test(o))
  if (ioflowPublish) plainOptions = plainOptions.filter((o) => !PUBLISH_RE.test(o))

  const submitTyped = () => {
    const v = value.trim()
    if (!v) return
    setValue('')
    onAnswer(v)
  }

  return (
    <div
      className={answered ? undefined : 'agent-ask-attention'}
      style={{
        background: 'var(--color-bg)',
        borderRadius: 'var(--radius-lg)',
        padding: '0.75rem',
        marginTop: '0.375rem',
        border: `1px solid ${answered ? 'var(--color-border)' : 'var(--color-accent)'}`,
        opacity: answered ? 0.85 : 1,
        maxWidth: '100%',
        minWidth: 0,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.375rem', marginBottom: '0.5rem' }}>
        <Sparkles size={12} style={{ color: 'var(--color-accent)', flexShrink: 0 }} />
        <span
          style={{
            fontSize: '0.9375rem',
            fontWeight: 600,
            color: answered ? undefined : 'var(--color-accent)',
          }}
        >
          Agent needs your input
        </span>
      </div>

      <p style={{ fontSize: '1rem', margin: '0 0 0.5rem', lineHeight: 1.5, overflowWrap: 'break-word' }}>
        {question}
      </p>

      {answered ? (
        <div
          style={{
            fontSize: '0.9375rem',
            color: 'var(--color-text-secondary)',
            fontStyle: 'italic',
            overflowWrap: 'break-word',
          }}
        >
          You answered: <strong style={{ fontStyle: 'normal' }}>{answeredWith}</strong>
        </div>
      ) : (
        <>
          {(plainOptions.length > 0 || formAvailable || ioflowConnect || ioflowPublish) && (
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'stretch',
                gap: '0.375rem',
                marginBottom: '0.5rem',
              }}
            >
              {formAvailable && (
                <button
                  className="btn btn-primary btn-sm"
                  onClick={() => setConnOpen(true)}
                  style={{
                    width: '100%',
                    justifyContent: 'flex-start',
                    textAlign: 'left',
                    whiteSpace: 'normal',
                    height: 'auto',
                    lineHeight: 1.4,
                  }}
                >
                  <Database size={12} style={{ flexShrink: 0 }} />
                  <span style={{ minWidth: 0 }}>
                    I will provide the connection — open the connection form
                  </span>
                </button>
              )}
              {ioflowConnect && (
                <button
                  className="btn btn-primary btn-sm"
                  onClick={() => setIoflowConnectOpen(true)}
                  style={{
                    width: '100%',
                    justifyContent: 'flex-start',
                    textAlign: 'left',
                    whiteSpace: 'normal',
                    height: 'auto',
                    lineHeight: 1.4,
                  }}
                >
                  <KeyRound size={12} style={{ flexShrink: 0 }} />
                  <span style={{ minWidth: 0 }}>
                    I will connect it — enter the credentials securely
                  </span>
                </button>
              )}
              {ioflowPublish && (
                <button
                  className="btn btn-primary btn-sm"
                  onClick={() => setIoflowPublishOpen(true)}
                  style={{
                    width: '100%',
                    justifyContent: 'flex-start',
                    textAlign: 'left',
                    whiteSpace: 'normal',
                    height: 'auto',
                    lineHeight: 1.4,
                  }}
                >
                  <ShieldCheck size={12} style={{ flexShrink: 0 }} />
                  <span style={{ minWidth: 0 }}>
                    Authorize publishing — code, or request approval
                  </span>
                </button>
              )}
              {plainOptions.map((opt) => (
                <button
                  key={opt}
                  className="btn btn-secondary btn-sm"
                  onClick={() => onAnswer(opt)}
                  style={{
                    width: '100%',
                    justifyContent: 'flex-start',
                    textAlign: 'left',
                    whiteSpace: 'normal',
                    overflowWrap: 'anywhere',
                    height: 'auto',
                    lineHeight: 1.4,
                  }}
                >
                  <ChevronRight size={12} style={{ flexShrink: 0 }} />
                  <span style={{ minWidth: 0 }}>{opt}</span>
                </button>
              ))}
            </div>
          )}

          <div style={{ display: 'flex', gap: '0.375rem' }}>
            <input
              autoFocus={options.length === 0}
              type="text"
              value={value}
              onChange={(e) => setValue(e.target.value)}
              placeholder={options.length > 0 ? 'Or type your own answer…' : 'Type your answer…'}
              onKeyDown={(e) => {
                if (e.key === 'Enter') submitTyped()
              }}
              style={{
                flex: 1,
                minWidth: 0,
                padding: '0.35rem 0.5rem',
                fontSize: '1rem',
                background: 'var(--color-bg-secondary)',
                border: '1px solid var(--color-border)',
                borderRadius: 'var(--radius-md)',
                color: 'var(--color-text)',
              }}
            />
            <button className="btn btn-primary btn-sm" onClick={submitTyped} disabled={!value.trim()}>
              Send
            </button>
          </div>

          {formAvailable && (
            <ConnectionDefineFlow
              project={project!}
              open={connOpen}
              onClose={() => setConnOpen(false)}
              onDefined={(conn) => {
                setConnOpen(false)
                onAnswer(connectionAnswerText(conn), conn.CONNECTION_ID)
              }}
            />
          )}

          {ioflowConnect && (
            <IOFlowConnectForm
              project={project!}
              open={ioflowConnectOpen}
              appId={appId}
              onClose={() => setIoflowConnectOpen(false)}
              onConnected={(reply) => {
                setIoflowConnectOpen(false)
                onAnswer(reply)
              }}
            />
          )}

          {ioflowPublish && (
            <IOFlowPublishForm
              project={project!}
              open={ioflowPublishOpen}
              seedJson={seedJson}
              onClose={() => setIoflowPublishOpen(false)}
              onPublished={(reply) => {
                setIoflowPublishOpen(false)
                onAnswer(reply)
              }}
              onRequestApproval={() => {
                setIoflowPublishOpen(false)
                // The agent files the request itself (it holds the seed
                // definition and the resume handle); this just tells it to.
                onAnswer(
                  'Please request approval from an IOFlow instance admin to ' +
                    'publish this connector, then stop and wait — I understand ' +
                    'you will continue automatically once it is decided.',
                )
              }}
            />
          )}
        </>
      )}
    </div>
  )
}
